import React, { lazy, Suspense } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Moon, MapPin } from 'lucide-react';
import { BortleBadge } from '@/components/locations/BortleBadge';
import { MoonCalendarView } from './MoonCalendarView';

const LocationMap = lazy(() => import('./LocationMap').then(m => ({ default: m.LocationMap })));

interface DashboardLocation {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  notes?: string;
  created_at?: string;
  bortle_class?: number | null;
  display_name?: string;
}

interface MoonPlannerViewProps {
  selectedLocation: DashboardLocation;
  onBackToSelection: () => void;
}

export const MoonPlannerView: React.FC<MoonPlannerViewProps> = ({ 
  selectedLocation, 
  onBackToSelection 
}) => {
  return (
    <div className="space-y-6">
      {/* Planner breadcrumb bar */}
      <div className="flex items-center justify-between py-3 border-b border-border">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBackToSelection}
            className="text-muted-foreground hover:text-foreground -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Locations
          </Button>
          <span className="text-border">/</span>
          <div className="flex items-center gap-2">
            <Moon className="w-4 h-4 text-gold" />
            <span className="font-semibold text-foreground">Moon Planner</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin className="w-3.5 h-3.5" />
          <span>{selectedLocation.display_name || selectedLocation.name}</span>
          <span className="text-muted-foreground/50 ml-1">
            {selectedLocation.latitude.toFixed(2)}°, {selectedLocation.longitude.toFixed(2)}°
          </span>
          {selectedLocation.bortle_class != null && (
            <BortleBadge bortle={selectedLocation.bortle_class} />
          )}
        </div>
      </div>

      <Suspense fallback={<div className="h-[400px] rounded-xl bg-muted animate-pulse" />}>
        <LocationMap
          latitude={selectedLocation.latitude}
          longitude={selectedLocation.longitude}
          locationName={selectedLocation.name}
          hideEmptyPrompt
        />
      </Suspense>

      <MoonCalendarView selectedLocation={selectedLocation} hideLocationCard />
    </div>
  );
};
